import { CURRENCY, OUTPOSTS, SHIP_CLASSES, TRADE_GOODS, type TradeGoodId } from '@sd/content';
import {
  capitalHex,
  hexDistance,
  outpostLimit,
  outpostSites,
  spaceportLevel,
  toLocal,
  type GameState,
  type OutpostSite,
  type PlayerState,
} from '@sd/sim';
import { savesTransportForSwarm } from './builder';
import { canBuy, threatened } from './fleets';
import type { Bot } from './match';
import { consumptionPerCycle } from './planner';

/** ГС the settler always leaves untouched, on top of the capital's import needs. */
const CUSHION = 400;
const IMPORT_CYCLES = 10;

/** Free sites nobody else's zone touches, best value per hex of distance first. */
function freeSites(
  state: GameState,
  playerId: string,
  prefer: TradeGoodId | null = null,
): OutpostSite[] {
  // The same outposts and finds give the same sites: worked out once, not on every look (#50).
  let byPlayer = sitesCache.get(state.outposts);
  if (!byPlayer) sitesCache.set(state.outposts, (byPlayer = new Map()));
  const key = `${playerId}:${prefer}`;
  const known = byPlayer.get(key);
  if (known && known.exploration === state.exploration) return known.sites;
  const sites = rankSites(state, playerId, prefer);
  byPlayer.set(key, { exploration: state.exploration, sites });
  return sites;
}

const sitesCache = new WeakMap<
  GameState['outposts'],
  Map<string, { exploration: GameState['exploration']; sites: OutpostSite[] }>
>();

function rankSites(state: GameState, playerId: string, prefer: TradeGoodId | null): OutpostSite[] {
  const home = capitalHex(state, playerId);
  const taken = new Set(state.outposts.map((o) => o.id));
  const value = (s: OutpostSite) =>
    OUTPOSTS[s.kind].output * TRADE_GOODS[s.good].basePrice * (s.good === prefer ? 10 : 1);
  return outpostSites(state.system, state.exploration)
    .filter((s) => !taken.has(s.id))
    .filter(
      (s) =>
        !state.outposts.some(
          (o) =>
            o.owner !== playerId &&
            hexDistance(o.hex, s.hex) <= OUTPOSTS[o.kind].zoneRadius + OUTPOSTS[s.kind].zoneRadius,
        ),
    )
    .sort(
      (a, b) =>
        value(b) / (1 + hexDistance(home, b.hex)) - value(a) / (1 + hexDistance(home, a.hex)) ||
        a.id.localeCompare(b.id),
    );
}

/** Spare ГС after the cushion and the capital's import needs. */
function spare(state: GameState, me: PlayerState): number {
  const use = consumptionPerCycle(me);
  const made = me.planet.lastCycle?.produced;
  const imports = (['food', 'electronics'] as const).reduce(
    (sum, g) => sum + Math.max(0, use[g] - (made?.[g] ?? 0)) * state.market[g].price,
    0,
  );
  const gsCash = (me.cash * me.currency.rate) / (1 + CURRENCY.conversionFee);
  return gsCash - CUSHION - imports * IMPORT_CYCLES;
}

/**
 * Phase 2 settler (GDD §5): while under its outpost limit, builds a
 * transport, flies it to the best free site and founds an outpost there.
 */
export function makeSettler(prefer: TradeGoodId | null): Bot {
  return (state, playerId, rng) => {
    if (state.pause || !rng.chance(0.05)) return [];
    const me = state.players.find((p) => p.id === playerId);
    if (!me) return [];
    // A transport is kept for the Dyson swarm once it can be built.
    if (savesTransportForSwarm(state, playerId)) return [];
    // No settling in wartime: transports carry troops then.
    if (threatened(state, playerId)) return [];
    const owned = state.outposts.filter((o) => o.owner === playerId).length;
    if (owned >= outpostLimit(spaceportLevel(me.planet))) return [];

    // Transports already running a contract's caravan are not the settler's to take.
    const caravans = new Set(
      state.contracts.flatMap((c) => (c.caravan ? [c.caravan.fleetId] : [])),
    );
    const carrier = state.fleets.find(
      (f) =>
        f.owner === playerId && !caravans.has(f.id) && f.ships.some((s) => s.cls === 'transport'),
    );
    if (!carrier) {
      if (me.shipyard.some((o) => o.ship === 'transport' || o.ship === 'tradeLiner')) return [];
      const target = freeSites(state, playerId, prefer)[0];
      if (!target) return [];
      const price =
        SHIP_CLASSES.transport.cost.cash + OUTPOSTS[target.kind].cost.cash * me.currency.rate;
      const alloys = SHIP_CLASSES.transport.cost.alloys + OUTPOSTS[target.kind].cost.alloys;
      const missing = Math.ceil(alloys - me.goods.alloys);
      const alloyCost = Math.max(0, missing) * state.market.alloys.price * 1.2;
      if (spare(state, me) < price + alloyCost) return [];
      // Buy the alloys the transport and the station take.
      if (missing > 0) {
        return canBuy(state, playerId, 'alloys')
          ? [{ type: 'marketBuy', playerId, good: 'alloys', quantity: missing }]
          : [];
      }
      return [{ type: 'buildShip', playerId, ship: 'transport' }];
    }
    if (carrier.path.length > 0) return [];

    const sites = freeSites(state, playerId, prefer);
    const here = sites.find((s) => s.hex.q === carrier.hex.q && s.hex.r === carrier.hex.r);
    if (here) {
      // Pay first (#107): asking without the alloys was refused every tick, 1 244 times a match.
      const cost = OUTPOSTS[here.kind].cost;
      const missing = Math.ceil(cost.alloys - me.goods.alloys);
      if (missing > 0) {
        return canBuy(state, playerId, 'alloys')
          ? [{ type: 'marketBuy', playerId, good: 'alloys', quantity: missing }]
          : [];
      }
      if (me.cash < toLocal(cost.cash, me.currency.rate)) return [];
      return [{ type: 'buildOutpost', playerId, fleetId: carrier.id }];
    }
    const target = sites[0];
    return target ? [{ type: 'moveFleet', playerId, fleetId: carrier.id, to: target.hex }] : [];
  };
}

export const settlerBot: Bot = makeSettler(null);
